import type { LucideIcon } from "lucide-react";
import type { ReactNode } from "react";

import { cn } from "@/lib/utils";

/**
 * Premium HealNova empty state — a soft brand halo behind a single icon,
 * a warm headline and a calm supporting line.
 */
export function EmptyState({
  icon: Icon,
  title,
  description,
  action,
  className,
}: {
  icon: LucideIcon;
  title: string;
  description?: string | undefined;
  action?: ReactNode;
  className?: string | undefined;
}) {
  return (
    <div
      className={cn(
        "flex flex-col items-center justify-center px-6 py-14 text-center sm:py-20",
        className,
      )}
    >
      <div className="relative grid size-20 place-items-center">
        <span
          className="absolute inset-0 rounded-full bg-primary/8 blur-[2px]"
          aria-hidden="true"
        />
        <span
          className="absolute inset-3 rounded-full border border-primary/15 bg-background"
          aria-hidden="true"
        />
        <span className="absolute -top-1 right-1 size-2.5 rounded-full bg-rose/60" aria-hidden />
        <span className="absolute bottom-0 left-0 size-1.5 rounded-full bg-info/50" aria-hidden />
        <Icon className="relative size-7 text-primary" aria-hidden="true" />
      </div>

      <h3 className="mt-6 font-display text-lg font-semibold text-balance">{title}</h3>
      {description && (
        <p className="mt-2 max-w-sm text-sm leading-relaxed text-muted-foreground text-pretty">
          {description}
        </p>
      )}
      {action && <div className="mt-6">{action}</div>}
    </div>
  );
}
